import React, { useMemo, useState } from 'react'
import {
  Box,
  Flex,
  Heading,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  VStack
} from '@chakra-ui/react'
import type { SliderValues } from './types'
import { calculateAllocation, DEFAULT_VALUES, SLIDER_RANGES, getCurrentPortfolioData, getIdealPortfolioData, normalizePortfolio } from './utils'
import usePortfolioCalculatorStyles from './styles'
import PieChart from './components/PieChart'

const PortfolioCalculator: React.FC = () => {
  const [values, setValues] = useState<SliderValues>(DEFAULT_VALUES)
  const result = useMemo(() => calculateAllocation(values), [values])
  const styles = usePortfolioCalculatorStyles()
  const currentData = useMemo(() => getCurrentPortfolioData(values), [values])
  const idealData = useMemo(() => getIdealPortfolioData(values, result.recommendedPercent), [values, result.recommendedPercent])

  const updateValue = (key: keyof SliderValues, value: number | SliderValues['assets']) => {
    if (key === 'currentBitcoin' && typeof value === 'number') {
      setValues(prev => {
        const normalized = normalizePortfolio(prev.assets, value)
        return {
          ...prev,
          assets: normalized.assets,
          currentBitcoin: normalized.bitcoin
        }
      })
    } else {
      setValues(prev => ({ ...prev, [key]: value }))
    }
  }

  const updateAsset = (assetKey: keyof SliderValues['assets'], value: number) => {
    setValues(prev => {
      const normalized = normalizePortfolio({ ...prev.assets, [assetKey]: value }, prev.currentBitcoin)
      return {
        ...prev,
        assets: normalized.assets,
        currentBitcoin: normalized.bitcoin
      }
    })
  }

  return (
    <>
    <Box {...styles.container}>
      <VStack {...styles.slidersSection}>
        <Box mt={8} w="100%">
          <Flex justify="space-around" wrap="wrap" gap={4}>
            <PieChart data={currentData} title="Current Portfolio" size={400} />
            <PieChart data={idealData} title="Ideal Portfolio" size={400} />
          </Flex>
        </Box>

        <Box mt={8} w="100%">
          <Heading size="md" mb={4}>Recommended Allocation</Heading>
          <Text fontSize="lg" fontWeight="bold" color="blue.500" mb={2}>
            {result.recommendedPercent.toFixed(1)}% Bitcoin
          </Text>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Confidence: {result.confidence}
          </Text>
          <Text fontSize="sm" mb={2}>
            {result.explanation}
          </Text>
          <Text fontSize="sm" fontWeight="bold">
            {result.allocationAdvice}
          </Text>
        </Box>

        {/* Age Slider */}
        <Box {...styles.sliderGroup}>
          <Text {...styles.sliderLabel}>Age: {values.age}</Text>
          <Slider
            value={values.age}
            min={SLIDER_RANGES.age.min}
            max={SLIDER_RANGES.age.max}
            step={SLIDER_RANGES.age.step}
            onChange={(value) => updateValue('age', value)}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
          <Text fontSize="xs" color="gray.600" mt={1}>
            Younger investors can afford more risk. Min: 18 (aggressive), Max: 80 (conservative)
          </Text>
        </Box>

        {/* Risk Tolerance Slider */}
        <Box {...styles.sliderGroup}>
          <Text {...styles.sliderLabel}>Risk Tolerance (1-10): {values.riskTolerance}</Text>
          <Slider
            value={values.riskTolerance}
            min={SLIDER_RANGES.riskTolerance.min}
            max={SLIDER_RANGES.riskTolerance.max}
            step={SLIDER_RANGES.riskTolerance.step}
            onChange={(value) => updateValue('riskTolerance', value)}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
          <Text fontSize="xs" color="gray.600" mt={1}>
            How much volatility can you handle? Min: 1 (very conservative), Max: 10 (very aggressive)
          </Text>
        </Box>

        {/* Asset Allocation Sliders - Note: These should ideally sum to 100% */}
        <Box {...styles.sliderGroup}>
          <Text {...styles.sliderLabel}>Current Portfolio (%)</Text>
          {Object.entries(values.assets).map(([key, value]) => (
            <Box key={key} mb={2}>
              <Text fontSize="xs">{key.charAt(0).toUpperCase() + key.slice(1)}: {value}%</Text>
              <Slider
                value={value}
                min={SLIDER_RANGES.assets.min}
                max={SLIDER_RANGES.assets.max}
                step={SLIDER_RANGES.assets.step}
                onChange={(val) => updateAsset(key as keyof SliderValues['assets'], val)}
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
            </Box>
          ))}
          <Box mb={2}>
            <Text fontSize="xs">Bitcoin: {values.currentBitcoin}%</Text>
            <Slider
              value={values.currentBitcoin}
              min={SLIDER_RANGES.currentBitcoin.min}
              max={SLIDER_RANGES.currentBitcoin.max}
              step={SLIDER_RANGES.currentBitcoin.step}
              onChange={(value) => updateValue('currentBitcoin', value)}
            >
              <SliderTrack>
                <SliderFilledTrack />
              </SliderTrack>
              <SliderThumb />
            </Slider>
          </Box>
          <Text fontSize="xs" color="gray.600" mt={1}>
            Breakdown of your current investments. Adjust to reflect your portfolio composition.
          </Text>
        </Box>

        {/* Income Slider */}
        <Box {...styles.sliderGroup}>
          <Text {...styles.sliderLabel}>Annual Income: ${values.income.toLocaleString()}</Text>
          <Slider
            value={values.income}
            min={SLIDER_RANGES.income.min}
            max={SLIDER_RANGES.income.max}
            step={SLIDER_RANGES.income.step}
            onChange={(value) => updateValue('income', value)}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
          <Text fontSize="xs" color="gray.600" mt={1}>
            Your total yearly earnings. Min: $0 (no income), Max: $500k+ (high income)
          </Text>
        </Box>

        {/* Expenses Slider */}
        <Box {...styles.sliderGroup}>
          <Text {...styles.sliderLabel}>Annual Expenses: ${values.expenses.toLocaleString()}</Text>
          <Slider
            value={values.expenses}
            min={SLIDER_RANGES.expenses.min}
            max={SLIDER_RANGES.expenses.max}
            step={SLIDER_RANGES.expenses.step}
            onChange={(value) => updateValue('expenses', value)}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
          <Text fontSize="xs" color="gray.600" mt={1}>
            Your yearly spending. Min: $0 (no expenses), Max: $500k+ (high expenses)
          </Text>
        </Box>

        {/* Investment Horizon Slider */}
        <Box {...styles.sliderGroup}>
          <Text {...styles.sliderLabel}>Investment Horizon (years): {values.horizon}</Text>
          <Slider
            value={values.horizon}
            min={SLIDER_RANGES.horizon.min}
            max={SLIDER_RANGES.horizon.max}
            step={SLIDER_RANGES.horizon.step}
            onChange={(value) => updateValue('horizon', value)}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
          <Text fontSize="xs" color="gray.600" mt={1}>
            How long until you need these funds? Min: 1 year (short-term), Max: 50 years (long-term)
          </Text>
        </Box>

        {/* Bitcoin Confidence Slider */}
        <Box {...styles.sliderGroup}>
          <Text {...styles.sliderLabel}>Bitcoin Confidence (1-10): {values.bitcoinConfidence}</Text>
          <Slider
            value={values.bitcoinConfidence}
            min={SLIDER_RANGES.bitcoinConfidence.min}
            max={SLIDER_RANGES.bitcoinConfidence.max}
            step={SLIDER_RANGES.bitcoinConfidence.step}
            onChange={(value) => updateValue('bitcoinConfidence', value)}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
          <Text fontSize="xs" color="gray.600" mt={1}>
            Your belief in Bitcoin's future value. Min: 1 (skeptical), Max: 10 (very bullish)
          </Text>
        </Box>

        {/* Expected Inflation Slider */}
        <Box {...styles.sliderGroup}>
          <Text {...styles.sliderLabel}>Expected Inflation (%): {values.expectedInflation}</Text>
          <Slider
            value={values.expectedInflation}
            min={SLIDER_RANGES.expectedInflation.min}
            max={SLIDER_RANGES.expectedInflation.max}
            step={SLIDER_RANGES.expectedInflation.step}
            onChange={(value) => updateValue('expectedInflation', value)}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
          <Text fontSize="xs" color="gray.600" mt={1}>
            Anticipated annual inflation rate. Min: 0% (no inflation), Max: 10% (high inflation)
          </Text>
        </Box>
      </VStack>
    </Box>
    </>
  )
}

export default PortfolioCalculator
